import React from 'react';
import './TemplateEditorPanel.css';
import useLocale from '../../hooks/useLocale';

const blockTypes = ['textarea', 'select'];

function TemplateEditorPanel({ template, setTemplate, draggedBlockType, setDraggedBlockType }) {
  const { getCurrentLanguage } = useLocale();
  const lang = getCurrentLanguage();

  const getBlockTypeLabel = (value) =>
    value === 'textarea'
      ? lang === 'ru'
        ? 'Текстовый блок'
        : 'Text block'
      : lang === 'ru'
        ? 'Селектор'
        : 'Select';

  const onDropNewBlock = (e) => {
    e.preventDefault();
    if (!draggedBlockType) return;
    const uniqueSuffix = Date.now();
    const newBlock = {
      id: 'block-' + uniqueSuffix,
      type: draggedBlockType,
      required: true,
      label:
        draggedBlockType === 'textarea'
          ? lang === 'ru'
            ? 'Новый текстовый блок'
            : 'New text block'
          : lang === 'ru'
            ? 'Новый селектор'
            : 'New select',
    };
    if (draggedBlockType === 'select') {
      newBlock.options = [
        {
          id: '1',
          label: lang === 'ru' ? 'Вариант 1' : 'Option 1',
        },
        {
          id: '2',
          label: lang === 'ru' ? 'Вариант 2' : 'Option 2',
        },
      ];
    }
    setTemplate((prev) => [...prev, newBlock]);
    setDraggedBlockType(null);
  };

  const onDragOver = (e) => {
    e.preventDefault();
  };

  const handleBlockChange = (id, value) => {
    console.log('[TemplateEditorPanel] handleBlockChange', id, value);
    setTemplate((prev) =>
      prev.map((block) => (block.id === id ? { ...block, label: value } : block)),
    );
  };

  const handleOptionChange = (blockId, optionIndex, value) => {
    setTemplate((prev) =>
      prev.map((block) => {
        if (block.id === blockId && block.options) {
          const newOptions = [...block.options];
          newOptions[optionIndex] = { ...newOptions[optionIndex], label: value };
          return { ...block, options: newOptions };
        }
        return block;
      }),
    );
  };

  const addOption = (blockId) => {
    setTemplate((prev) =>
      prev.map((block) => {
        if (block.id === blockId && block.options) {
          const uniqueSuffix = Date.now();
          return {
            ...block,
            options: [
              ...block.options,
              {
                id: `option_${uniqueSuffix}`,
                label: lang === 'ru' ? 'Новый вариант' : 'New option',
              },
            ],
          };
        }
        return block;
      }),
    );
  };

  const deleteOption = (blockId, optionIndex) => {
    setTemplate((prev) =>
      prev.map((block) => {
        if (block.id === blockId && block.options) {
          const newOptions = block.options.filter((_, idx) => idx !== optionIndex);
          return { ...block, options: newOptions };
        }
        return block;
      }),
    );
  };

  const deleteBlock = (id) => {
    setTemplate((prev) => prev.filter((block) => block.id !== id));
  };

  const moveBlockUp = (index) => {
    if (index <= 0) return;
    setTemplate((prev) => {
      const newTemplate = [...prev];
      [newTemplate[index - 1], newTemplate[index]] = [newTemplate[index], newTemplate[index - 1]];
      return newTemplate;
    });
  };

  const moveBlockDown = (index) => {
    if (index >= template.length - 1) return;
    setTemplate((prev) => {
      const newTemplate = [...prev];
      [newTemplate[index], newTemplate[index + 1]] = [newTemplate[index + 1], newTemplate[index]];
      return newTemplate;
    });
  };

  return (
    <div className="editor-panel-container">
      <div className="block-bank">
        <h3>{lang === 'ru' ? 'Банк блоков' : 'Block bank'}</h3>
        <div className="bank-items">
          {blockTypes.map((value) => (
            <div
              key={value}
              className="bank-item"
              draggable
              onDragStart={(e) => {
                setDraggedBlockType(value);
                e.dataTransfer.setData('text/plain', value);
              }}
            >
              {getBlockTypeLabel(value)}
            </div>
          ))}
        </div>
      </div>
      <div className="constructor-area" onDrop={onDropNewBlock} onDragOver={onDragOver}>
        <h3>{lang === 'ru' ? 'Конструктор шаблона' : 'Template constructor'}</h3>
        {template.length === 0 && (
          <div className="empty-constructor">
            {lang === 'ru'
              ? 'Пусто. Перетащите блоки из банка.'
              : 'Empty. Drag blocks from the bank.'}
          </div>
        )}
        {template.map((block, idx) => (
          <div key={block.id} className="block">
            <div className="block-header">
              <span className="block-type">{getBlockTypeLabel(block.type)}</span>
              <div className="block-actions">
                <button onClick={() => moveBlockUp(idx)} className="move-btn">
                  ↑
                </button>
                <button onClick={() => moveBlockDown(idx)} className="move-btn">
                  ↓
                </button>
                <button onClick={() => deleteBlock(block.id)} className="delete-block">
                  {lang === 'ru' ? 'Удалить' : 'Delete'}
                </button>
              </div>
            </div>
            <div className="block-body">
              <label>
                {lang === 'ru' ? 'Заголовок:' : 'Title:'}
                <input
                  type="text"
                  value={block.label}
                  onChange={(e) => handleBlockChange(block.id, e.target.value)}
                />
              </label>

              <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
                <input
                  type="checkbox"
                  checked={block.required !== false}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setTemplate((prev) =>
                      prev.map((b) => (b.id === block.id ? { ...b, required: checked } : b)),
                    );
                  }}
                />
                {lang === 'ru' ? 'Обязательное поле' : 'Required field'}
              </label>
              {block.type === 'select' && (
                <div className="options-container">
                  <h4>{lang === 'ru' ? 'Варианты выбора' : 'Options'}</h4>
                  {block.options.map((opt, optIndex) => (
                    <div key={optIndex} className="option">
                      <div className="option-fields">
                        <label className="option-label">
                          Label:
                          <input
                            type="text"
                            value={opt.label}
                            onChange={(e) => handleOptionChange(block.id, optIndex, e.target.value)}
                          />
                        </label>
                      </div>
                      <button
                        onClick={() => deleteOption(block.id, optIndex)}
                        className="delete-option"
                      >
                        {lang === 'ru' ? 'Удалить вариант' : 'Delete option'}
                      </button>
                    </div>
                  ))}
                  <button onClick={() => addOption(block.id)} className="add-option">
                    {lang === 'ru' ? 'Добавить вариант' : 'Add option'}
                  </button>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default TemplateEditorPanel;
